import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowRight,
  FaBuilding,
  FaClock,
  FaEnvelope,
  FaExclamation,
  FaListUl,
  FaPaperclip,
  FaSave,
  FaTag,
  FaCheckCircle,
  FaTicketAlt,
  FaTimes,
  FaUsers
} from 'react-icons/fa';
import AssignableUsersSelect from '../components/AssignableUsersSelect';
import { AuthContext } from '../context/AuthContext';
import branchService from '../services/branchService';
import ticketService from '../services/ticketService';
import userService from '../services/userService';
import { normalizeAssignableUserOptions } from '../utils/assignableUsers';
import { ISSUE_TYPE_OPTIONS } from '../utils/issueTypes';
import { normalizeRole, ROLE } from '../utils/rbac';
import {
  buildApiErrorMessage,
  buildTicketDateTimePayload,
  UPLOAD_ACCEPT_ATTRIBUTE,
  validateTicketUploadFile
} from '../utils/ticketForm';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'منخفضة' },
  { value: 'medium', label: 'متوسطة' },
  { value: 'high', label: 'عالية' },
  { value: 'urgent', label: 'عاجلة' }
];

const CHANNEL_OPTIONS = [
  { value: '', label: 'اختر القناة' },
  { value: 'email', label: 'بريد إلكتروني' },
  { value: 'whatsapp', label: 'واتساب' },
  { value: 'phone', label: 'هاتف' },
  { value: 'web', label: 'شخصية' }
];

const VALIDATION_MESSAGES = {
  branch: 'يرجى اختيار الفرع',
  issueTypes: 'يرجى اختيار نوع مشكلة واحد على الأقل',
  description: 'يرجى كتابة وصف المشكلة',
  createFailed: 'فشل إنشاء التذكرة'
};

const CreateTicket = () => {
  const [formData, setFormData] = useState({
    branch_id: '',
    issue_types: [],
    priority: 'medium',
    channel: '',
    short_description: '',
    custom_received_date: '',
    custom_received_time: '',
    is_resolved: false,
    resolution_notes: ''
  });
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const attachmentInputRef = useRef(null);

  const { user: currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const currentUserRole = useMemo(
    () => normalizeRole(currentUser?.role),
    [currentUser?.role]
  );

  const assignmentHelperText = useMemo(() => {
    if (currentUserRole === ROLE.TECHNICIAN) {
      return 'يمكنك ترك التذكرة بدون تكليف أو إسنادها إلى نفسك.';
    }

    return 'تشمل قائمة المكلفين المتاحين الفنيين والمشرفين ومديري النظام.';
  }, [currentUserRole]);

  useEffect(() => {
    fetchBranches();
    fetchAssignableUsers();
  }, []);

  const fetchBranches = async () => {
    try {
      const data = await branchService.getAll();
      setBranches(data.branches || data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const data = await userService.getTicketAssignableUsers();
      setAssignableUsers(normalizeAssignableUserOptions(data.users || data));
    } catch (err) {
      console.error(err);
      setAssignableUsers([]);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const processedValue = name === 'branch_id'
      ? (value ? Number.parseInt(value, 10) : '')
      : value;

    setFormData((previous) => ({ ...previous, [name]: processedValue }));
  };

  const handleIssueTypeChange = (issueTypeValue, checked) => {
    setFormData((previous) => ({
      ...previous,
      issue_types: checked
        ? [...previous.issue_types, issueTypeValue]
        : previous.issue_types.filter((type) => type !== issueTypeValue)
    }));
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files?.[0] || null;
    const validationMessage = validateTicketUploadFile(file);

    setAttachmentError(validationMessage);

    if (validationMessage) {
      setAttachmentFile(null);
      event.target.value = '';
      return;
    }

    setAttachmentFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (!formData.branch_id) {
      setError(VALIDATION_MESSAGES.branch);
      setLoading(false);
      return;
    }

    if (formData.issue_types.length === 0) {
      setError(VALIDATION_MESSAGES.issueTypes);
      setLoading(false);
      return;
    }

    if (!formData.short_description.trim()) {
      setError(VALIDATION_MESSAGES.description);
      setLoading(false);
      return;
    }

    if (attachmentError) {
      setError(attachmentError);
      setLoading(false);
      return;
    }

    if (formData.is_resolved && !formData.resolution_notes.trim()) {
      setError('يرجى كتابة تفاصيل الحل عند تحديد خيار التذكرة محلولة بالفعل.');
      setLoading(false);
      return;
    }

    const receivedAtPayload = buildTicketDateTimePayload({
      date: formData.custom_received_date,
      time: formData.custom_received_time,
      autoFillNow: true,
      missingMessage: 'يرجى إدخال تاريخ ووقت الإنشاء معاً أو تركهما فارغين.'
    });

    if (receivedAtPayload.error) {
      setError(receivedAtPayload.error);
      setLoading(false);
      return;
    }

    try {
      const selectedAssigneeIds = selectedAssignees.map((assignee) => assignee.value);
      const createResponse = await ticketService.create({
        branch_id: formData.branch_id,
        issue_types: formData.issue_types,
        priority: formData.priority,
        channel: formData.channel,
        short_description: formData.short_description,
        assigned_to: selectedAssigneeIds[0] || null,
        assignee_ids: selectedAssigneeIds,
        received_at: receivedAtPayload.value,
        is_resolved: formData.is_resolved,
        resolution_notes: formData.resolution_notes
      });

      const createdTicketId = createResponse?.ticket?.id;

      if (attachmentFile && createdTicketId) {
        try {
          await ticketService.uploadAttachment(createdTicketId, attachmentFile);
          setSuccessMessage('تم إنشاء التذكرة ورفع المرفق بنجاح.');
          window.setTimeout(() => navigate('/tickets'), 1500);
        } catch (uploadError) {
          setSuccessMessage('تم إنشاء التذكرة بنجاح.');
          setError(`${buildApiErrorMessage(uploadError, 'تعذر رفع المرفق.')} يمكنك إعادة المحاولة من صفحة التذكرة.`);
          window.setTimeout(() => navigate(`/tickets/${createdTicketId}`), 2000);
        }
      } else {
        setSuccessMessage('تم إنشاء التذكرة بنجاح. جارٍ التحويل إلى القائمة...');
        window.setTimeout(() => navigate('/tickets'), 1500);
      }

      setAttachmentFile(null);
      setAttachmentError('');
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
    } catch (err) {
      setError(buildApiErrorMessage(err, VALIDATION_MESSAGES.createFailed));
      console.error('Create ticket error:', err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="page-container ticket-create-container">
      <div className="dashboard-header-modern">
        <div className="header-content">
          <div className="header-icon">
            <FaTicketAlt />
          </div>
          <div className="header-text">
            <h1>إنشاء تذكرة جديدة</h1>
            <p className="welcome-text">تسجيل بلاغ جديد وربطه بالفنيين المكلفين</p>
          </div>
        </div>

        <Button
          variant="outline-secondary"
          className="btn-action-view"
          onClick={() => navigate('/tickets')}
          title="العودة إلى قائمة التذاكر"
        >
          <FaListUl className="btn-icon" />
          <span>قائمة التذاكر</span>
        </Button>
      </div>

      <Card className="dashboard-card ticket-form-card operational-form-card">
        <Card.Header className="dashboard-card-header operational-form-header">
          <div className="card-header-title">
            <FaTicketAlt className="section-icon" />
            <span>نموذج إنشاء التذكرة</span>
          </div>
          <span className="text-muted small">
            الحقول المعلّمة بـ <span className="required">*</span> إلزامية
          </span>
        </Card.Header>

        <Card.Body className="p-4 p-lg-5">
          {error && <Alert variant="danger" className="alert-modern">{error}</Alert>}
          {successMessage && (
            <Alert variant="success" className="alert-modern">
              {successMessage}
            </Alert>
          )}

          <Form onSubmit={handleSubmit} className="ticket-form">
            <div className="form-section">
              <div className="form-section-header">
                <FaBuilding className="section-icon" />
                <h3>المعلومات الأساسية</h3>
              </div>
              <p className="form-section-description">
                بيانات الاستلام الأساسية التي تحدد الفرع والأولوية وقناة وصول البلاغ.
              </p>

              <Row className="form-row g-3">
                <Col md={6} lg={4}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label">
                      <FaBuilding className="label-icon" />
                      الفرع <span className="required">*</span>
                    </Form.Label>
                    <Form.Select
                      name="branch_id"
                      value={formData.branch_id}
                      onChange={handleChange}
                      required
                      isInvalid={!formData.branch_id && Boolean(error)}
                      className="form-field-input"
                    >
                      <option value="">اختر الفرع</option>
                      {branches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </Form.Select>
                    {error && !formData.branch_id && (
                      <div className="field-error">{VALIDATION_MESSAGES.branch}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6} lg={4}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label">
                      <FaExclamation className="label-icon" />
                      الأولوية
                    </Form.Label>
                    <Form.Select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      className="form-field-input"
                    >
                      {PRIORITY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6} lg={4}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label">
                      <FaEnvelope className="label-icon" />
                      قناة الاستلام
                    </Form.Label>
                    <Form.Select
                      name="channel"
                      value={formData.channel}
                      onChange={handleChange}
                      className="form-field-input"
                    >
                      {CHANNEL_OPTIONS.map((option) => (
                        <option key={option.value || 'empty'} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="form-section">
              <div className="form-section-header">
                <FaTag className="section-icon" />
                <h3>نوع المشكلة <span className="required">*</span></h3>
              </div>
              <p className="form-section-description">
                يمكنك اختيار أكثر من نوع لضمان دقة التصنيف والتوجيه.
              </p>

              <div className="issue-types-grid">
                {ISSUE_TYPE_OPTIONS.map((issueType) => (
                  <Form.Check
                    key={issueType.value}
                    type="checkbox"
                    id={`issue-type-${issueType.value}`}
                    label={issueType.label}
                    checked={formData.issue_types.includes(issueType.value)}
                    onChange={(event) => handleIssueTypeChange(issueType.value, event.target.checked)}
                    className="issue-type-checkbox"
                  />
                ))}
              </div>

              {error && formData.issue_types.length === 0 && (
                <div className="field-error">{VALIDATION_MESSAGES.issueTypes}</div>
              )}
            </div>

            <div className="form-section">
              <div className="form-section-header">
                <FaTag className="section-icon" />
                <h3>وصف المشكلة <span className="required">*</span></h3>
              </div>
              <p className="form-section-description">
                اكتب وصفًا واضحًا ومختصرًا يساعد الفريق الفني على البدء بسرعة.
              </p>

              <Form.Group className="form-field">
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleChange}
                  required
                  placeholder="اكتب وصفًا مختصرًا للمشكلة..."
                  className="form-field-input"
                />
              </Form.Group>
            </div>

            <div className="form-section">
              <div className="form-section-header">
                <FaClock className="section-icon" />
                <h3>تخصيص تاريخ ووقت الاستلام</h3>
              </div>
              <p className="form-section-description">
                اختياري: استخدمه عندما يكون وقت الاستلام مختلفًا عن وقت الإدخال الفعلي.
              </p>

              <Row className="form-row g-3">
                <Col md={6}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label">تاريخ الاستلام</Form.Label>
                    <Form.Control
                      type="date"
                      name="custom_received_date"
                      value={formData.custom_received_date}
                      onChange={handleChange}
                      className="form-field-input"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label">وقت الاستلام</Form.Label>
                    <Form.Control
                      type="time"
                      name="custom_received_time"
                      value={formData.custom_received_time}
                      onChange={handleChange}
                      className="form-field-input"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Text className="text-muted">
                اترك الحقلين فارغين لاستخدام التاريخ والوقت الحاليين.
              </Form.Text>
            </div>

            <div className="form-section">
              <div className="form-section-header">
                <FaPaperclip className="section-icon" />
                <h3>المرفق الأولي</h3>
              </div>
              <p className="form-section-description">
                اختياري: يمكنك إرفاق ملف مع التذكرة عند الإنشاء.
              </p>

              <Form.Group className="form-field">
                <Form.Label className="field-label">
                  <FaPaperclip className="label-icon" />
                  اختر الملف
                </Form.Label>
                <Form.Control
                  ref={attachmentInputRef}
                  type="file"
                  accept={UPLOAD_ACCEPT_ATTRIBUTE}
                  onChange={handleAttachmentChange}
                  className="form-field-input"
                />
                <Form.Text className="text-muted">
                  الأنواع المدعومة: {UPLOAD_ACCEPT_ATTRIBUTE} - الحد الأقصى 50 MB.
                </Form.Text>
                {attachmentError && <div className="field-error">{attachmentError}</div>}
              </Form.Group>
            </div>

            <div className="form-section">
              <div className="form-section-header">
                <FaUsers className="section-icon" />
                <h3>الفنيون المكلفون</h3>
                <span className="selected-count">({selectedAssignees.length} محدد)</span>
              </div>
              <p className="form-section-description">
                تحديد الفنيين من البداية يقلل زمن الاستجابة ويحسن المتابعة.
              </p>

              <AssignableUsersSelect
                label="المكلفون بالتذكرة"
                options={assignableUsers}
                value={selectedAssignees}
                onChange={setSelectedAssignees}
                placeholder="اختر مستخدمًا واحدًا أو أكثر للتكليف"
                searchPlaceholder="ابحث بالاسم أو اسم المستخدم أو الصلاحية"
                noOptionsMessage="لا توجد نتائج مطابقة"
                noAvailableMessage="لا يوجد مستخدمون متاحون للتكليف"
                helperText={assignmentHelperText}
                disabled={loading}
              />
            </div>

            <div className="form-section">
              <div className="form-section-header">
                <FaCheckCircle className="section-icon text-success" />
                <h3>حل مباشر (التذاكر المنتهية)</h3>
              </div>
              <p className="form-section-description">
                اختياري: إذا تم حل المشكلة مسبقاً، يمكنك إضافة الحل هنا لإغلاق التذكرة فور إنشائها.
              </p>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="direct-resolve-switch"
                  label="إضافة تفاصيل الحل وإغلاق التذكرة"
                  checked={formData.is_resolved}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_resolved: e.target.checked }))}
                />
              </Form.Group>

              {formData.is_resolved && (
                <Form.Group className="form-field">
                  <Form.Label className="field-label">
                    ملخص الحل <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="resolution_notes"
                    value={formData.resolution_notes}
                    onChange={handleChange}
                    required={formData.is_resolved}
                    placeholder="اكتب خطوات الحل التي تمت..."
                    className="form-field-input"
                  />
                </Form.Group>
              )}
            </div>

            <div className="form-actions ticket-form-action-bar">
              <Button variant="primary" className="btn-action-save" type="submit" disabled={loading}>
                <FaSave className="btn-icon" />
                {loading ? 'جارٍ إنشاء التذكرة...' : 'إنشاء التذكرة'}
              </Button>

              <Button
                variant="secondary"
                className="btn-action-cancel"
                onClick={() => navigate('/tickets')}
                disabled={loading}
              >
                <FaTimes className="btn-icon" />
                إلغاء
              </Button>

              <Button
                variant="outline-secondary"
                className="btn-action-view"
                onClick={() => navigate('/tickets')}
                disabled={loading}
              >
                <FaArrowRight className="btn-icon" />
                العودة إلى القائمة
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CreateTicket;
